import React, { useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { TopProductsCompareRequest, TopProductsCompareResponse, useTopComparedProduct } from '../../../../api/indicators';
import { dateRange, formatPeriodLabel } from '../../../../helpers/dateUtils';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { DashboardContext } from '../../DashboardContext';
import { TableChip } from '../../../../components/EnhancedTableToolbar';
import ComparedPieBarChart, { ComparedPieBarChartData } from '../comparedCharts/ComparedPieBarChart';
import { UserContext } from '../../../../context/UserContext';



const DEFAULT_LIMIT_PRODUCTS = 10;

const ComparedNetMarginExcTaxByProduct = () => {
    const { t } = useTranslation();
    const { locale } = useContext(UserContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);

    const [params, setParams] = useState<TopProductsCompareRequest>({
        fields: 'netMargin',
        startDateTime: filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
        endDateTime: filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
        compareStartDateTime: filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate,
        compareEndDateTime: filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate,
        locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
        limit: DEFAULT_LIMIT_PRODUCTS
    });

    const {
        data: dataTopProductCompared,
        isLoading,
        isError,
        refetch: fetchTopProductsCompared
    } = useTopComparedProduct<TopProductsCompareResponse>(params, 'net-margin-top-products/compared');

    useEffect(() => {
        setParams(p => ({
            ...p,
            startDateTime: filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
            endDateTime: filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
            compareStartDateTime: filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate,
            compareEndDateTime: filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate,
            locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id
        }));
    }, [filters, dateRangeConfig, companyConfig]);

    useEffect(() => {
        fetchTopProductsCompared();
    }, [params, fetchTopProductsCompared]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchTopProductsCompared();
        }
    }, [refreshDate, fetchTopProductsCompared]);

    function transformData(data: TopProductsCompareResponse | undefined): ComparedPieBarChartData[] {
        // no data
        if (!data || (!data?.topProducts.netMargin && !data?.comparedTopProducts.netMargin)) return [];

        // no comparison data
        if (!data.comparedTopProducts) {
            return data.topProducts.netMargin.map(item => ({
                label: item.productName,
                value: item.netMargin ?? 0,
                comparedValue: 0
            }));
        }

        // no current data
        if (!data.topProducts) {
            return data.comparedTopProducts.netMargin.map(item => ({
                label: item.productName,
                value: 0,
                comparedValue: item.netMargin ?? 0
            }));
        }

        // both periods have data
        const currentDataMap = new Map(
            data.topProducts.netMargin.map(item => [item.productName, item.netMargin])
        );

        const comparedDataMap = new Map(
            data?.comparedTopProducts?.netMargin?.map(item => [item.productName, item.netMargin])
        );

        const allKeys = new Set([
            ...(data.topProducts.netMargin?.map(item => item.productName) || []),
            ...(data.comparedTopProducts.netMargin?.map(item => item.productName) || [])
        ]);

        return Array.from(allKeys).map(key => ({
            label: key,
            value: currentDataMap.get(key) ?? 0,
            comparedValue: comparedDataMap.get(key) ?? 0,
        }));
    }

    const currentChip: TableChip = useMemo(() =>
    ({
        label: `${formatPeriodLabel(
            new Date(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
            locale
        )} - ${formatPeriodLabel(new Date(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
        labelType: 'period'
    }), [filters.periodFrom, filters.periodTo]);

    const compareChip: TableChip = useMemo(() =>
    ({
        label: `${formatPeriodLabel(
            new Date(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
            locale
        )} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
        labelType: 'periodCompare'
    }), [filters.comparePeriodFrom, filters.comparePeriodTo]);

    return (
        <Box>
            <ComparedPieBarChart
                title={t('screens.dashboarding.charts.netMarginByProduct')}
                description={t('screens.dashboarding.charts.excludingTax')}
                currency={dataTopProductCompared?.currency}
                data={transformData(dataTopProductCompared)}
                pieLabels={[
                    `${formatPeriodLabel(new Date(filters.periodFrom ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.periodTo ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                    `${formatPeriodLabel(new Date(filters.comparePeriodFrom ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`
                ]}
                locale={locale}
                isLoading={isLoading}
                error={isError}
                height={590}
                reportName={t('screens.dashboarding.charts.salesByProduct')}
                reportLink={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/DailySales.aspx`}
                chips={[currentChip, compareChip]}
            />
        </Box >
    )
}

export default ComparedNetMarginExcTaxByProduct;
